import { t } from "../i18n/t";

export type Column<T> = {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  numeric?: boolean;
  /** The redesign's action column has a header only for screen readers. */
  hiddenHeader?: boolean;
  /** Extra class on this column's cells (e.g. `cell--wide`: spans the whole card row when the table stacks). */
  cellClassName?: string;
};

/** §B7.1: a real table element, with a caption and `scope="col"` on every header. */
export function Table<T>({
  caption,
  columns,
  rows,
  rowKey,
  emptyMessage,
  bare = false,
  visibleCaption = false,
  rowClassName,
  rowDataKey,
  wrap = false,
  className,
}: {
  caption: string;
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  emptyMessage?: string;
  /** Skip the `.table-card` frame — the table sits inside a card (or frame) its caller already rendered. */
  bare?: boolean;
  /** The redesign draws no visible caption, except the framed mini-tables (Settings' sweep counts). */
  visibleCaption?: boolean;
  rowClassName?: (row: T) => string | undefined;
  /** xmg-webux: the row's business key, rendered as `data-rowkey` (and the row made focusable) so a
   * "go to this row" action elsewhere on the page can scroll to it and move focus onto it. */
  rowDataKey?: (row: T) => string;
  /** xmg-review-layout: let cells wrap (long names, GUID keys, sentences) instead of the global `nowrap`, so
   * the table fits its card and never needs a sideways scrollbar. Numeric cells stay on one line. */
  wrap?: boolean;
  /** Extra class on the `<table>` (a screen's own column widths / stacked-card rules). */
  className?: string;
}): JSX.Element {
  const table = (
    <div className={wrap ? "table-scroll table-scroll--wrap" : "table-scroll"}>
      <table className={className}>
        <caption className={visibleCaption ? undefined : "visually-hidden"}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" className={column.numeric === true ? "n" : undefined}>
                {column.hiddenHeader === true ? (
                  <span className="visually-hidden">{column.header}</span>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>{emptyMessage ?? t("table.empty")}</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={rowClassName?.(row)}
                data-rowkey={rowDataKey?.(row)}
                tabIndex={rowDataKey === undefined ? undefined : -1}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={
                      [column.numeric === true ? "n mono" : "", column.cellClassName ?? ""]
                        .filter((name) => name !== "")
                        .join(" ") || undefined
                    }
                    data-label={column.header}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
  return bare ? table : <div className="table-card">{table}</div>;
}
